import { gql } from "lib/apolloClient";
import {
  attachmentFragment,
  staffFragment,
  taggedPracticesFragment,
} from "modules/CommonFragments";
import { questionFragments } from "Worksheet/modules/WorksheetFragments";

export const resourceLibraryFragment = {
  resourceFeed: gql`
    fragment resourceLibraryItem on FlowSectionItem {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      templateId
      ... on Assessment {
        assessmentType {
          id
          value
        }
        academicTerm {
          id
          lockingStatus
        }
        assessmentTools {
          edges {
            node {
              id
              assessmentToolType
            }
          }
        }
        grades {
          id
          name
        }

        fields(
          uids: ["duration", "size", "measureAssessing", "assessmentType"]
        ) {
          id
          uid
          value
        }
        isImported
        taggedPractices {
          ...taggedPracticesOnEvidence
        }
      }

      ... on LearningEngagement {
        fields(uids: ["duration", "size", "documentation"]) {
          id
          uid
          value
        }
      }
    }
    ${taggedPracticesFragment.taggedPractices}
    ${staffFragment.basicStaffDetails}
  `,
};

export const organizationResourceFragment = {
  organizationResource: gql`
    fragment organizationResourceItem on OrganizationResource {
      id
      type
      label
      attachment {
        ...attachmentItem
      }
      childrenV2(orderBy: CREATED_AT, orderByDirection: ASC) {
        resourceTypeBasedCount {
          totalCount
        }
        edges {
          node {
            id
            item {
              ...questionDetailsItem
            }
            childrenV2(orderBy: CREATED_AT, orderByDirection: ASC) {
              resourceTypeBasedCount {
                totalCount
              }
              edges {
                node {
                  id
                  item {
                    ...questionDetailsItem
                  }
                }
              }
            }
          }
        }
      }
      isImported: isImportedFromDiffOrg
      isDeleted
      type
      resourceType
      visibleTo
      createdAt
      updatedAt
      state
      studentDriveSharedCourses {
        id
        title
        profileImage
        academicYears {
          id
          isCurrentAcademicYear
          startDate
          endDate
        }
      }
      item {
        ... on OrganizationResource {
          id
          label
          isImported: isImportedFromDiffOrg
          attachment {
            ...attachmentItem
          }
          isDeleted
          type
          resourceType
          visibleTo
        }
        ... on Assessment {
          ...resourceLibraryItem
        }
        ... on EntityFlowResource {
          id
          label
          attachment {
            ...attachmentItem
          }
        }
      }
      tags {
        id
        key
        value
      }
    }
    ${questionFragments.questionDetails}
    ${resourceLibraryFragment.resourceFeed}
    ${attachmentFragment.attachment}
  `,
  unitFlowResources: gql`
    fragment unitFlowResourcesItem on OrganizationResource {
      id
      resourceType
      type
      label
      isImported: isImportedFromDiffOrg
      children {
        id
        label
        type
        resourceType
        item {
          id
          ... on OrganizationResource {
            id
            label
            type
            resourceType
          }
          ... on Assessment {
            id
          }
        }
      }
    }
  `,
  basicDetails: gql`
    fragment organizationResourceBasicDetailsItem on OrganizationResource {
      id
      label
      attachment {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const organizationChildResourceFragment = {
  childResourceFragment: gql`
    fragment organizationChildResourceItem on OrganizationResource {
      id
      label
      children(
        visibleTo: $visibleTo
        searchText: $searchText
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: $filters
      ) {
        ...organizationResourceItem
      }
      isDeleted
      visibleTo
      resourceType
      parentHierarchy {
        id
        label
      }
      tags {
        key
        id
        value
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
  childResourceFragmentV2: gql`
    fragment organizationChildResourceItemV2 on OrganizationResource {
      id
      label
      children: childrenV2(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        filters: $filters
      ) {
        resourceTypeBasedCount {
          folderCount
          fileCount
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...organizationResourceItem
          }
        }
      }
      isDeleted
      visibleTo
      resourceType
      parentHierarchy {
        id
        label
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
};

export const organizationResourceFeedFragment = {
  organizationResourceFeed: gql`
    fragment organizationResourceFeedItem on Organization {
      id
      organizationResources(
        types: $types
        visibleTo: $visibleTo
        searchText: $searchText
        filters: $organizationResourceFilter
      ) {
        ...organizationResourceItem
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
  unitPlanResourceFeed: gql`
    fragment unitPlanResourceFeedItem on UnitPlan {
      id
      organizationResources(searchText: $searchText) {
        ...organizationResourceItem
      }
      unitFlowResources {
        ...unitFlowResourcesItem
      }
    }
    ${organizationResourceFragment.organizationResource}
    ${organizationResourceFragment.unitFlowResources}
  `,
  unitPlanResourceFeedV2: gql`
    fragment unitPlanResourceFeedItem on UnitPlan {
      id
      organizationResourcesConnection(
        searchText: $searchText
        after: $after
        first: 12
      ) @skip(if: $isUnitFlow) {
        edges {
          node {
            ...organizationResourceItem
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        resourceTypeBasedCount {
          totalCount
        }
      }
      unitFlowResources @include(if: $isUnitFlow) {
        ...unitFlowResourcesItem
      }
    }
    ${organizationResourceFragment.organizationResource}
    ${organizationResourceFragment.unitFlowResources}
  `,
  assessmentResourceFeed: gql`
    fragment assessmentResourceFeedItem on Assessment {
      id
      organizationResources {
        ...organizationResourceItem
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
  studentDriveResourceFeed: gql`
    fragment studentDriveResourceFeedItem on Course {
      id
      organizationResources: studentDriveV2(
        filters: {
          types: $types
          states: $states
          visibleTo: $visibleTo
          searchText: $searchText
          showOnlyRecentlyShared: $showOnlyRecentlyShared
          searchParentResourceId: $searchParentResourceId
          searchParentResourceType: $searchParentResourceType
        }
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        first: $first
        after: $after
      ) {
        resourceTypeBasedCount {
          folderCount
          fileCount
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...organizationResourceItem
          }
        }
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
};
